import { dia } from '@joint/core'
import type { MapObject, Geometry } from '../../../../map-editor-entities/schema/types'
import { extractRelations } from './relationshipExtractor'

/**
 * Convert JointJS element to MapObject
 */
export function convertElementToMapObject(element: dia.Element): MapObject {
  const data = element.get('data') || {}
  const position = element.position()
  const size = element.size()

  // Determine geometry based on element data type and stored vertices
  let geometry: Geometry
  const elementType = data.type as string | undefined
  const vertices = data.vertices as Array<[number, number]> | undefined

  if ((elementType === 'polygon' || elementType === 'line') && vertices && vertices.length >= 2) {
    // Polygon/line elements: stored vertices are absolute coords at creation time.
    // If user dragged the element, element.position() moved but vertices did not.
    // Apply delta from data.originalPosition to make export reflect current position.
    const origPos = data.originalPosition as { x: number; y: number } | undefined
    const dx = origPos ? position.x - origPos.x : 0
    const dy = origPos ? position.y - origPos.y : 0
    const adjustedVertices: Array<[number, number]> = (dx !== 0 || dy !== 0)
      ? vertices.map(v => [v[0] + dx, v[1] + dy] as [number, number])
      : vertices
    geometry = {
      type: elementType === 'polygon' ? 'polygon' as const : 'polyline' as const,
      coordinates: adjustedVertices,
      ...(elementType === 'polygon' ? { closed: true } : {})
    } as Geometry
  } else if ((elementType === 'polygon' || elementType === 'line') && !vertices) {
    // Backward compat: try parsing SVG path from attrs for older saved projects
    const pathD = element.attr('body/d') as string | undefined
    const parsedVertices = pathD ? parseSVGPathToAbsoluteCoords(pathD, position) : undefined
    if (parsedVertices && parsedVertices.length >= 2) {
      geometry = {
        type: elementType === 'polygon' ? 'polygon' as const : 'polyline' as const,
        coordinates: parsedVertices,
        ...(elementType === 'polygon' ? { closed: true } : {})
      } as Geometry
    } else {
      // Final fallback: point geometry at center
      geometry = {
        type: 'point' as const,
        coordinates: [position.x + size.width / 2, position.y + size.height / 2] as [number, number]
      }
    }
  } else {
    // Point-based elements (assets, icons)
    geometry = {
      type: 'point' as const,
      coordinates: [position.x + size.width / 2, position.y + size.height / 2] as [number, number]
    }
  }

  return {
    id: 0, // buildMapData 가 export 시 1부터 순차 int 로 재할당한다
    type: data.typeId || data.objectType || data.type || 'unknown',
    name: data.properties?.name || data.text || `Object-${(element.id as string).slice(0, 8)}`,
    layer: data.objectType || data.layer || 'default',
    entityHandle: data.entityHandle,
    geometry,
    style: {
      color: data.style?.color,
      fillColor: data.style?.fillColor,
      strokeColor: data.style?.strokeColor,
      strokeWidth: data.style?.strokeWidth,
      opacity: data.style?.opacity,
      zIndex: data.style?.zIndex
    },
    properties: {
      ...(data.properties || {}),
      __size: { width: size.width, height: size.height }
    },
    assetRefs: data.assetRefs,
    relations: extractRelations(data.properties || {})
  }
}

/**
 * Parse SVG path data (relative M/L commands) back to absolute coordinates.
 * Used as backward compatibility for elements that don't have stored vertices.
 */
export function parseSVGPathToAbsoluteCoords(
  pathD: string,
  elementPosition: { x: number; y: number }
): Array<[number, number]> | undefined {
  try {
    const commands = pathD.match(/[ML]\s*[\d.eE+-]+\s+[\d.eE+-]+/g)
    if (!commands || commands.length < 2) return undefined

    return commands.map(cmd => {
      const nums = cmd.match(/[\d.eE+-]+/g)
      if (!nums || nums.length < 2) return [0, 0] as [number, number]
      // Path coords are relative to element position
      return [
        parseFloat(nums[0]) + elementPosition.x,
        parseFloat(nums[1]) + elementPosition.y
      ] as [number, number]
    })
  } catch {
    return undefined
  }
}
